import { usePropertyContext } from "@app/app/hooks";
import { IBookingEngineState } from "@app/app/types";
import { useGetCountries } from "@app/common/hooks";
import { VendorLoyaltyProvider } from "@app/loyalty/constants/VendorLoyaltyProvider";
import { ILoyaltyContext, ILoyaltyMemberDetails, LoyaltyContext } from "@app/loyalty/context/LoyaltyContext";
import { useLocalStorage } from "@app/loyalty/loyaltyLocalStorage";
import { getBookingWhizzMemberDetails } from "@app/loyalty/queries";
import React, { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

export interface ILoyaltyContextProviderProps {
    children?: React.ReactNode | React.ReactNode[];
}

export const LoyaltyContextProvider = ({ children }: ILoyaltyContextProviderProps) => {
    const { activePropertyId, activeProperty } = usePropertyContext();
    const [index, setIndex] = useState<string>("login");
    // Todo: Replace local storage solution with cookie solution when available
    const [accessToken, setAccessToken] = useLocalStorage<string>({
        key: "loyaltyAccessToken",
        initialValue: "",
        prefix: "loyalty",
        expiresIn: 1000 * 60 * 60 * 24 * 7,
    }); // 7 days
    const [memberDetails, setMemberDetails] = useState<ILoyaltyMemberDetails | null>(null);
    const { setValue } = useFormContext<IBookingEngineState>();
    const { data: countries } = useGetCountries();

    useEffect(() => {
        if (
            accessToken !== "" &&
            memberDetails === null &&
            activeProperty?.vendor_loyalty_provider === VendorLoyaltyProvider.BookingWhizz
        ) {
            // If the access token is set, we can fetch member details
            getBookingWhizzMemberDetails(activePropertyId, accessToken).then((details: ILoyaltyMemberDetails) => {
                // Check if the details contain a valid tier name
                if (details.tierName) {
                    setMemberDetails(details);
                    setIndex("profile");

                    // Set promo code if available
                    if (details.promocode) {
                        setValue("promoCode", details.promocode);
                    }
                } else {
                    // 'Logout' if no valid tier name is found
                    setAccessToken("");
                    setMemberDetails(null);
                    setIndex("login");
                }
            });
        }
    }, [accessToken]);

    const api: ILoyaltyContext = {
        index,
        accessToken,
        memberDetails,
        countries: countries ?? [],
        setIndex: useCallback(setIndex, [setIndex]),
        setAccessToken,
        setMemberDetails: useCallback(setMemberDetails, [setMemberDetails]),
    };

    return <LoyaltyContext.Provider value={api}>{children}</LoyaltyContext.Provider>;
};
